/**
 * Thumbnail backfill — walks every Card row missing an `Image URL` and queries
 * the Pokémon TCG API (or Scryfall for MTG) to find the matching card's image.
 *
 * Throttled to ~5 requests/second to stay polite — well under the per-user
 * rate limits even without an API key. Cancellable via the returned token.
 *
 * Sealed product isn't auto-backfilled because there's no equivalent API for
 * sealed product images; users paste URLs manually on the Sealed form.
 */
import { DB } from "./db";
import { Settings } from "./prefs";
import { toast } from "./dom";
import { searchableCardName } from "./fmv";

export interface BackfillProgress {
  done: number;
  total: number;
  filled: number;
  skipped: number;
  failed: number;
  currentName?: string;
}

export interface BackfillToken {
  cancel: () => void;
  promise: Promise<BackfillProgress>;
}

const THROTTLE_MS = 200;  // ~5 req/s, safe even without an API key

async function lookupPokemonImage(name: string, setName: string, number: string): Promise<string | null> {
  const qParts: string[] = [];
  if (name)    qParts.push(`name:"${searchableCardName(name).replace(/"/g, "")}"`);
  if (setName) qParts.push(`set.name:"${setName.replace(/"/g, "")}"`);
  if (number)  qParts.push(`number:${number.replace(/[^0-9a-zA-Z]/g, "")}`);
  if (!qParts.length) return null;
  const q = qParts.join(" ");
  const url = `https://api.pokemontcg.io/v2/cards?q=${encodeURIComponent(q)}&pageSize=1`;
  const headers: Record<string, string> = {};
  const key = Settings.get().pokemonTcgApiKey;
  if (key) headers["X-Api-Key"] = key;
  const r = await fetch(url, { headers });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const j = await r.json();
  const card = (j.data && j.data[0]) as any;
  return (card && card.images && (card.images.small || card.images.large)) || null;
}

async function lookupMtgImage(name: string, setName: string): Promise<string | null> {
  if (!name) return null;
  const q = setName
    ? `!"${name.replace(/"/g, "")}" set:"${setName.replace(/"/g, "")}"`
    : `!"${name.replace(/"/g, "")}"`;
  const url = `https://api.scryfall.com/cards/search?q=${encodeURIComponent(q)}&unique=prints&order=released&dir=desc`;
  const r = await fetch(url);
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const j = await r.json();
  const card = (j.data && j.data[0]) as any;
  const img =
    (card?.image_uris && (card.image_uris.normal || card.image_uris.small)) ||
    (card?.card_faces?.[0]?.image_uris?.normal);
  return img || null;
}

/** Start backfilling card thumbnails. Caller can cancel mid-run. */
export function startBackfill(
  onProgress: (p: BackfillProgress) => void,
): BackfillToken {
  let cancelled = false;
  const cancel = () => { cancelled = true; };

  const promise = (async (): Promise<BackfillProgress> => {
    const rows = DB.table("cards").filter(r => {
      // Skip bulk pool singletons and rows that already have an image.
      const id = r["Card Lot ID"];
      const isBulk = id === "BULK" || id === "HOLO-BULK" || id === "PREM-BULK" || id === "BULK-1-5" || id === "BULK-5-10";
      return !isBulk && !r["Image URL"] && (r["Card Name"] || "").trim();
    });
    const progress: BackfillProgress = {
      done: 0, total: rows.length, filled: 0, skipped: 0, failed: 0,
    };
    onProgress({ ...progress });

    for (const row of rows) {
      if (cancelled) break;
      progress.currentName = row["Card Name"];
      onProgress({ ...progress });
      const game = row["Game"] || "Pokemon";
      const name = (row["Card Name"] || "").trim();
      const setName = (row["Set"] || "").trim();
      const number = (row["Card #"] || "").trim();
      try {
        let img: string | null = null;
        if (game === "Pokemon")                   img = await lookupPokemonImage(name, setName, number);
        else if (game === "Magic: The Gathering") img = await lookupMtgImage(name, setName);
        else {
          progress.skipped++;
          progress.done++;
          onProgress({ ...progress });
          continue;
        }
        if (img) {
          DB.update("cards", "Card Lot ID", row["Card Lot ID"], { "Image URL": img });
          progress.filled++;
        } else {
          progress.skipped++;
        }
      } catch (e: any) {
        console.error(`[backfill] ${row["Card Name"]}:`, e);
        progress.failed++;
      }
      progress.done++;
      onProgress({ ...progress });
      // Polite throttle before the next request
      if (!cancelled && progress.done < rows.length) {
        await new Promise<void>(res => setTimeout(res, THROTTLE_MS));
      }
    }

    progress.currentName = undefined;
    onProgress({ ...progress });
    toast(`Backfill ${cancelled ? "cancelled" : "complete"} · ${progress.filled} filled, ${progress.skipped} skipped, ${progress.failed} failed`);
    return progress;
  })();

  return { cancel, promise };
}
